/** @jsxRuntime classic */
/** @jsx jsx */
import React, { Component, Fragment } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';

import { RootState } from 'reducers/rootReducer';
import {
  messages, cannotGoBackDialog, toolbarTopTextConfig as text, settingsTextConfig, createNewSketchDialog
} from 'config/messages';
import { sketchConfig } from 'config/sketchConfig';
import { isTouchDevice } from 'helpers/browserDetect';
import { logout, beforeLogout, returnToWorkbench, isDesktopApplication, isWorkbenchApplication, isMobileApplication, } from 'effects/auth';

import { actions as numpadModalActions, selectors as numpadModalSelectors } from 'ducks/modal/numpadModal';
import { selectors as glaBreakdownModalSelectors, actions as glaBreakdownModalActions } from 'ducks/modal/glaBreakdownModal';
import { selectors as settingsModalSelectors, actions as settingsModalActions } from 'ducks/modal/settingsModal';
import { selectors as sketchSearchModalSelectors, actions as sketchSearchModalActions } from 'ducks/modal/sketchSearchModal';
import { selectors as pageGroupsModalSelectors, actions as pageGroupsModalActions } from 'ducks/modal/pageGroupsModal';
import { selectors as printPreviewModalSelectors, actions as printPreviewModalActions } from 'ducks/modal/printPreviewModal';
import { actions as messageContainerActions, selectors as messageContainerSelectors, onSubmitEventType } from 'ducks/messageContainer';
import { actions as resizeSketchModalActions, selectors as resizeSketchModalSelectors } from 'ducks/modal/resizeSketchModal';
import { actions as unsavedModalActions } from 'ducks/modal/unsavedModal';
import { selectors as sidebarSelectors, actions as sidebarActions } from 'ducks/sidebar/sidebar';
import { selectors as figureSelectors, } from 'ducks/model/figures';
import { actions as historyActions, selectors as historySelectors } from 'ducks/history/history';
import { actions as editModeActions } from 'ducks/editMode';
import { actions as sketchPersistenceActions } from 'ducks/persistence/sketchPersistence';
import { actions as bluePrintImageActions } from 'ducks/bluePrintImage/bluePrintImage';
import { actions as inspectionActions } from 'ducks/inspection';
import { selectors as orderDetailSelectors, } from 'ducks/orderDetails/orderDetails';
import { actions as sketchActions } from 'ducks/model/sketch';

import { ReactComponent as ShowHideIconActive } from 'assets/icons/Sketchpad Icon black in white circle.svg';
import { ReactComponent as IconUndo } from 'assets/icons/undo.svg';
import { ReactComponent as IconRedo } from 'assets/icons/redo.svg';
import { ReactComponent as IconPages } from 'assets/icons/pages-solid.svg';
import { ReactComponent as IconCalculator } from 'assets/icons/calculate-line-fill.svg';
import { ReactComponent as IconUpload } from 'assets/icons/upload.svg';
import { ReactComponent as IconSave } from 'assets/icons/save.svg';
import { ReactComponent as IconLogout } from 'assets/icons/logout.svg';
import { ReactComponent as IconUploadImage } from 'assets/icons/image.svg';
import { ReactComponent as IconCog } from 'assets/icons/settings.svg';
import { ReactComponent as IconNumpad } from 'assets/icons/numpad.svg';
import { ReactComponent as Hamburger } from 'assets/icons/hamburger.svg';
import { ReactComponent as CreateNew } from 'assets/icons/create-new.svg';

import { FromSketchMessageType } from 'types/sketch';

import MobileMenu from 'components/mobileMenu/MobileMenu';
import { TooltipContent } from 'components/elements/Tooltip';

import AciHeader from 'figma/components/header/aciheader';
import { aciColors } from 'figma/styles/acistylecolors';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ToolbarButton, { ProtectedButton as ToolbarProtectedButton } from './ToolbarButton';

const TOOLTIP_POSITION = 'top';
const MENU_TOOLTIP_POSITION = 'left';
const MOBILE_MENU_TOOLTIP_POSITION = 'right';

const mediaQueries = css`
  color: white;
  fill: white;

  &:hover,
  &:focus {
    color: ${aciColors.grey900};
    fill: ${aciColors.grey900};
  }

  & {
    @media (max-width: 960px) {
      margin-left: 4px;
      margin-right: 4px;
    }
    @media (max-width: 840px) {
      margin-left: 2px;
      margin-right: 2px;
    }
    @media (max-height: 800px) {
      margin-top: 4px;
      margin-bottom: 4px;
    }
  }
`;

const sketchPadTextStyles = css`
 font-family: "Public Sans", sans-serif;
 color: white;
`;

const Button = styled(ToolbarButton)(mediaQueries);
const ProtectedButton = styled(ToolbarProtectedButton)(mediaQueries);

const Section = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  color: #1a244f;
`;

const active = css`
  color: ${aciColors.faFoundationBlue};
  fill: ${aciColors.faFoundationBlue};
  background-color: white;
`;

const activeIcon = css`
 svg circle {
    fill: white !important;
  }
  svg path {
    fill: ${aciColors.faFoundationBlue} !important;
  }
    &:hover {
    svg circle {
      fill: ${aciColors.faFoundationBlue} !important;
    }
    svg path {
      fill: white !important;
    }
  }
`;

const Logo = styled.h1`
  margin: 0 10px;
  line-height: 50px;
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
  user-select: none;

  @media (min-width: 800px) {
    font-size: 20px;
  }
`;

const flexAlignCenter = css`
  margin-left: auto;
  margin-right: auto;
`;

const flexAlignLeft = css`
  margin-right: auto;
`;

const Separator = styled.div`
  width: 1px;
  border-left: 1px solid #e5e6ec;
  height: 40px;
`;

const hiddenInput = css({
  display: 'none',
});

interface StateProps {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly isShowingMobileMenu: boolean;
  readonly isShowingToolbarLeft: boolean;
  readonly isShowingGlaBreakdownModal: boolean;
  readonly isShowingSettingsModal: boolean;
  readonly isShowingSketchSearchModal: boolean;
  readonly isShowingPageGroupsModal: boolean;
  readonly isShowingPrintPreviewModal: boolean;
  readonly isShowingNumpadModal: boolean;
  readonly highlightBack: boolean;
  readonly getOrderId: string;
}

interface ActionProps {
  readonly loadSketch: () => void;
  readonly addImage: (imageUrl: string, width: number, height: number) => void;
  readonly showGlaBreakdownModal: () => void;
  readonly hideGlaBreakdownModal: () => void;
  readonly showNumpadModal: () => void;
  readonly hideNumpadModal: () => void;
  readonly showSketchSearchModal: () => void;
  readonly hideSketchSearchModal: () => void;
  readonly showSettingsModal: () => void;
  readonly hideSettingsModal: () => void;
  readonly showPageGroupsModal: () => void;
  readonly hidePageGroupsModal: () => void;
  readonly hidePrintPreviewModal: () => void;
  readonly showKnownLengthPromptModal: () => void;
  readonly toggleToolbarLeft: () => void;
  readonly toggleMobileMenu: () => void;
  readonly hideSidebar: () => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly close: () => void;
  readonly clearHistory: () => void;
  readonly showMessage: (title: string, message: string | JSX.Element, onSubmit: onSubmitEventType, onSubmitText: string) => void;
  readonly toggleIsTracingFirstWall: typeof resizeSketchModalActions.toggleIsTracingFirstWall;
  readonly toInspectionMessage: typeof inspectionActions.toInspectionMessage;
  readonly setSketch: typeof sketchPersistenceActions.setSketch;
  readonly autoSaveSketch: typeof sketchPersistenceActions.autoSaveSketch;
  readonly clearSelection: typeof editModeActions.clearSelection;
  readonly showUnsavedModal: typeof unsavedModalActions.show;
}

type Props = StateProps & ActionProps;

type State = {
  windowWidth: number;
}

class ToolbarTop extends Component<Props, State> {
  private readonly inputOpenFileRef: React.RefObject<HTMLInputElement>;

  private menuRef: React.RefObject<HTMLElement>;

  private autoSaveIntervalId?: ReturnType<typeof setInterval>;

  public constructor(props: Props) {
    super(props);
    this.state = {
      windowWidth: window.innerWidth,
    };

    this.menuRef = React.createRef();
    this.inputOpenFileRef = React.createRef();

    // allow a hosting app to call the localSave
    const doLocalSave = this.handleLocalSave;
    window.doSessionSave = function doSessionSave() {
      doLocalSave();
      localStorage.setItem('isDirty', 'false');
    };
  }

  private componentDidMount = () => {
    window.addEventListener('resize', this.handleWindowResize);
    this.autoSaveIntervalId = setInterval(() => {
      this.autoSaveLocalAction();
    }, 20_000);
  }

  private componentWillUnmount = () => {
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.autoSaveIntervalId) {
      clearInterval(this.autoSaveIntervalId);
    }
  }

  private autoSaveLocalAction = (): void => {
    const { autoSaveSketch } = this.props;
    autoSaveSketch();
    localStorage.setItem('isDirty', 'false');
    const now = new Date();
    console.log(`AutoSaved at ${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`);
  }

  private handleWindowResize = () => {
    this.setState({ windowWidth: window.innerWidth });
  }

  private toggleGlaBreakdownModal = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    const {
      isShowingGlaBreakdownModal,
      isShowingSketchSearchModal,
      isShowingSettingsModal,
      isShowingPageGroupsModal,
      isShowingPrintPreviewModal,
      isShowingNumpadModal,
      hideNumpadModal,
      hideGlaBreakdownModal,
      hideSketchSearchModal,
      hideSettingsModal,
      hidePageGroupsModal,
      hidePrintPreviewModal,
      showGlaBreakdownModal,
      hideSidebar,
    } = this.props;
    if (isShowingGlaBreakdownModal) {
      hideGlaBreakdownModal();
    } else {
      showGlaBreakdownModal();
      if (isShowingPageGroupsModal) hidePageGroupsModal();
      if (isShowingPrintPreviewModal) hidePrintPreviewModal();
      if (isShowingSettingsModal) hideSettingsModal();
      if (isShowingNumpadModal) hideNumpadModal();
      if (isShowingSketchSearchModal) hideSketchSearchModal();
    }
    hideSidebar();
  };

  private toggleOnScreenNumPad = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    const {
      isShowingGlaBreakdownModal,
      isShowingSettingsModal,
      isShowingSketchSearchModal,
      isShowingPageGroupsModal,
      isShowingPrintPreviewModal,
      isShowingNumpadModal,
      hideNumpadModal,
      hideGlaBreakdownModal,
      hideSketchSearchModal,
      hideSettingsModal,
      hidePageGroupsModal,
      hidePrintPreviewModal,
      showNumpadModal,
      hideSidebar
    } = this.props;
    if (isShowingNumpadModal) {
      hideNumpadModal();
    } else {
      showNumpadModal();
      if (isShowingGlaBreakdownModal) hideGlaBreakdownModal();
      if (isShowingPageGroupsModal) hidePageGroupsModal();
      if (isShowingPrintPreviewModal) hidePrintPreviewModal();
      if (isShowingSettingsModal) hideSettingsModal();
      if (isShowingSketchSearchModal) hideSketchSearchModal();
    }
    hideSidebar();
  };

  private sendNotification = (): void => {
    const { toInspectionMessage } = this.props;
    toInspectionMessage({ type: FromSketchMessageType.Ping });
  };

  private togglePageGroupsModal = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    const {
      isShowingPageGroupsModal,
      isShowingGlaBreakdownModal,
      isShowingSettingsModal,
      isShowingSketchSearchModal,
      isShowingPrintPreviewModal,
      isShowingNumpadModal,
      hideNumpadModal,
      hidePageGroupsModal,
      hideGlaBreakdownModal,
      hideSketchSearchModal,
      hideSettingsModal,
      hidePrintPreviewModal,
      showPageGroupsModal,
      hideSidebar,
      clearSelection
    } = this.props;
    if (isShowingPageGroupsModal) {
      hidePageGroupsModal();
    } else {
      showPageGroupsModal();
      if (isShowingGlaBreakdownModal) hideGlaBreakdownModal();
      if (isShowingPrintPreviewModal) hidePrintPreviewModal();
      if (isShowingSettingsModal) hideSettingsModal();
      if (isShowingNumpadModal) hideNumpadModal();
      if (isShowingSketchSearchModal) hideSketchSearchModal();
    }
    clearSelection();
    hideSidebar();
  };

  private toggleSettingsModal = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    const {
      isShowingSettingsModal,
      isShowingSketchSearchModal,
      isShowingPageGroupsModal,
      isShowingGlaBreakdownModal,
      isShowingPrintPreviewModal,
      isShowingNumpadModal,
      hideNumpadModal,
      hidePageGroupsModal,
      hideGlaBreakdownModal,
      hideSettingsModal,
      hideSketchSearchModal,
      hidePrintPreviewModal,
      showSettingsModal,
      hideSidebar
    } = this.props;
    if (isShowingSettingsModal) {
      hideSettingsModal();
    } else {
      showSettingsModal();
      if (isShowingGlaBreakdownModal) hideGlaBreakdownModal();
      if (isShowingPrintPreviewModal) hidePrintPreviewModal();
      if (isShowingPageGroupsModal) hidePageGroupsModal();
      if (isShowingNumpadModal) hideNumpadModal();
      if (isShowingSketchSearchModal) hideSketchSearchModal();
    }
    hideSidebar();
  };

  private toggleSketchSearchModal = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    const {
      isShowingSettingsModal,
      isShowingSketchSearchModal,
      isShowingPageGroupsModal,
      isShowingGlaBreakdownModal,
      isShowingPrintPreviewModal,
      isShowingNumpadModal,
      hideNumpadModal,
      hidePageGroupsModal,
      hideGlaBreakdownModal,
      hideSettingsModal,
      hideSketchSearchModal,
      hidePrintPreviewModal,
      showSketchSearchModal,
      hideSidebar,
      clearSelection
    } = this.props;
    if (isShowingSketchSearchModal) {
      hideSketchSearchModal();
    } else {
      showSketchSearchModal();
      if (isShowingGlaBreakdownModal) hideGlaBreakdownModal();
      if (isShowingPrintPreviewModal) hidePrintPreviewModal();
      if (isShowingPageGroupsModal) hidePageGroupsModal();
      if (isShowingNumpadModal) hideNumpadModal();
      if (isShowingSettingsModal) hideSettingsModal();
    }
    clearSelection();
    hideSidebar();
  };

  private logoutUser = async (event: React.MouseEvent<HTMLElement>): Promise<void> => {
    const { getOrderId, showUnsavedModal } = this.props;
    // if there are unsaved changed, ask if the user wants them saved.
    if (localStorage.getItem('isDirty')) {
      showUnsavedModal();
      return;
    }

    // If launched from Workbench, return to the Order
    if (isWorkbenchApplication()) {
      await returnToWorkbench({ beforeLogout }, getOrderId());
      return;
    }

    if (!isTouchDevice && !!event) event.preventDefault();
    await logout({ beforeLogout });
  };

  private toggleToolbarLeft = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    const { toggleToolbarLeft } = this.props;
    toggleToolbarLeft();
    this.onClose();
  };

  private toggleMobileMenu = (event: any): void => {
    this.menuRef = event.currentTarget;
    const { toggleMobileMenu } = this.props;
    toggleMobileMenu();
    this.onClose();
  };

  private handleMobileMenuClick = (event: any, onClick: any): void => {
    const { toggleMobileMenu } = this.props;
    if (onClick) onClick();
    toggleMobileMenu();
    this.onClose();
  };

  private onClose = (): void => {
    const { close } = this.props;
    close();
  };

  private handleLocalSave = (): void => {
    const { setSketch, clearSelection } = this.props;
    clearSelection();
    setTimeout(setSketch, 1);
  };

  private undo = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    const { undo } = this.props;
    undo();
  };

  private redo = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    const { redo } = this.props;
    redo();
  };

  private toggleUploadImageModal = (event: React.MouseEvent<HTMLElement>): void => {
    if (!isTouchDevice && !!event) event.preventDefault();
    this.inputOpenFileRef.current!.click();
  };

  private handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files[0]) {
      const { addImage, showKnownLengthPromptModal, toggleIsTracingFirstWall } = this.props;
      const imageUrl = URL.createObjectURL(event.target.files[0]);

      const img = new Image();
      img.onload = () => {
        addImage(imageUrl, img.width, img.height);
        showKnownLengthPromptModal();
        toggleIsTracingFirstWall(true);
      };
      img.onerror = () => {
        toast.error(messages.cannotAddBluePrintImage);
      };
      img.src = imageUrl;
    }
  };

  private handleFileClick = (): void => {
    if (this.inputOpenFileRef.current !== null) {
      this.inputOpenFileRef.current.value = '';
    }
  };

  private openCreateSketchWindow = (event: React.MouseEvent<HTMLElement>): void => {
    const { showMessage, clearSketch, clearHistory, resetCurrentSketch } = this.props;
    const createNewSketch = (): void => {
      clearSketch();
      clearHistory();
      resetCurrentSketch();
    };

    if (!isTouchDevice && !!event) event.preventDefault();
    showMessage(createNewSketchDialog.title, createNewSketchDialog.text, createNewSketch, createNewSketchDialog.submitText);
  };

  private renderMenuOptions = [
    {
      title: text.pages,
      onClick: this.togglePageGroupsModal,
      displayed: true,
      icon: <IconPages />,
    },
    {
      title: text.touchLabels.calculations,
      onClick: this.toggleGlaBreakdownModal,
      displayed: true,
      icon: <IconCalculator />,
    },
    {
      title: text.touchLabels.numpad,
      onClick: this.toggleOnScreenNumPad,
      displayed: true,
      icon: <IconNumpad />,
    },
    {
      title: text.touchLabels.addImage,
      onClick: this.toggleUploadImageModal,
      displayed: true,
      disabled: true,
      icon: <IconUploadImage />,
    },
    {
      title: text.touchLabels.quickSave,
      onClick: this.handleLocalSave,
      displayed: isDesktopApplication() && !isMobileApplication(),
      icon: <IconSave />,
    },
    {
      title: text.search,
      onClick: this.toggleSketchSearchModal,
      displayed: !isDesktopApplication() && !isMobileApplication(),
      icon: <CloudUploadIcon />,
    },
    {
      title: text.touchLabels.createSketch,
      onClick: this.openCreateSketchWindow,
      displayed: isDesktopApplication() && !isMobileApplication(),
      icon: <CreateNew />,
    },
    {
      title: settingsTextConfig.heading,
      onClick: this.toggleSettingsModal,
      displayed: true,
      icon: <IconCog />,
    },
    {
      title: isWorkbenchApplication() ? text.returnHelper : text.logoutHelper,
      onClick: this.logoutUser,
      displayed: sketchConfig.showLogoutButton && !isDesktopApplication() && !isMobileApplication(),
      icon: <IconLogout />,
    },
  ];

  public render(): JSX.Element {
    const {
      canUndo,
      canRedo,
      isShowingSketchSearchModal,
      isShowingPageGroupsModal,
      isShowingGlaBreakdownModal,
      isShowingSettingsModal,
      isShowingToolbarLeft,
      isShowingMobileMenu,
      isShowingNumpadModal,
      highlightBack,
      loadSketch
    } = this.props;

    const { windowWidth } = this.state;

    const useCompressedMenu = windowWidth < 700;

    return (
      <div>
        <AciHeader>
          <Button
            css={activeIcon}
            iconSize={35}
            onClick={this.toggleToolbarLeft}
            tooltip={<h5>{text.toggleToolbarLeft}</h5>}
            tooltipPosition={MENU_TOOLTIP_POSITION}
            tooltipShow={highlightBack}
            active={isShowingToolbarLeft}
            icon={<ShowHideIconActive />}
          />

          <Section css={flexAlignLeft}>
            <Logo css={sketchPadTextStyles}>
              Sketchpad
            </Logo>
          </Section>

          <Section css={flexAlignCenter}>
            <Button
              iconSize={24}
              disabled={!canUndo}
              onClick={this.undo}
              tooltip={<h5>{text.undo}</h5>}
              tooltipPosition={TOOLTIP_POSITION}
              tooltipShow={highlightBack}
            >
              <IconUndo />
            </Button>
            <Button iconSize={24} disabled={!canRedo} onClick={this.redo} tooltip={<h5>{text.redo}</h5>} tooltipPosition={TOOLTIP_POSITION}>
              <IconRedo />
            </Button>
          </Section>
          {useCompressedMenu ? (
            <Fragment>
              <Separator />
              <Button
                css={{ minWidth: 40, paddingLeft: 5 }}
                iconSize={18}
                onClick={this.toggleMobileMenu}
                tooltip={<h5>{text.toggleMobileMenu}</h5>}
                tooltipPosition={MOBILE_MENU_TOOLTIP_POSITION}
                tooltipShow={highlightBack}
                href={this.menuRef}
                icon={<Hamburger />}
              />
              <div id="sketch-mobile-menu-root">
                {isShowingMobileMenu && <MobileMenu menuList={this.renderMenuOptions} handleClose={this.handleMobileMenuClick} />}
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <Section>
                <Button
                  css={isShowingPageGroupsModal && active}
                  onClick={this.togglePageGroupsModal}
                  aria-label={text.pages}
                  iconSize={24}
                  touchLabel={text.pages}
                  tooltipPosition={TOOLTIP_POSITION}
                  tooltip={<TooltipContent title={text.pages} />}
                >
                  <IconPages />
                </Button>
                <Button
                  css={isShowingGlaBreakdownModal && active}
                  onClick={this.toggleGlaBreakdownModal}
                  aria-label={text.calculations}
                  iconSize={24}
                  touchLabel={text.touchLabels.calculations}
                  tooltipPosition={TOOLTIP_POSITION}
                  tooltip={<TooltipContent title={text.calculations} />}
                >
                  <IconCalculator />
                </Button>
                <Button
                  css={isShowingNumpadModal && active}
                  onClick={this.toggleOnScreenNumPad}
                  aria-label={text.numpad}
                  iconSize={24}
                  touchLabel={text.touchLabels.numpad}
                  tooltipPosition={TOOLTIP_POSITION}
                  tooltip={<TooltipContent title={text.numpad} />}
                >
                  <IconNumpad />
                </Button>
              </Section>
              <Separator />
              <Section>
                <Button
                  onClick={this.toggleUploadImageModal}
                  aria-label={text.addImage}
                  iconSize={24}
                  disabled
                  touchLabel={text.touchLabels.addImage}
                  tooltipPosition={TOOLTIP_POSITION}
                  tooltip={<TooltipContent title={text.addImage} />}
                >
                  <IconUploadImage />
                </Button>
                {sketchConfig.showLoadButton && (
                  <ProtectedButton
                    onClick={loadSketch}
                    iconSize={24}
                    touchLabel={text.upload}
                    tooltipPosition={TOOLTIP_POSITION}
                    tooltip={<TooltipContent title={text.upload} />}
                  >
                    <IconUpload />
                  </ProtectedButton>
                )}

                {isDesktopApplication() && (
                  <Button
                    onClick={this.handleLocalSave}
                    iconSize={24}
                    touchLabel={text.touchLabels.quickSave}
                    tooltipPosition={TOOLTIP_POSITION}
                    tooltip={<TooltipContent title={text.quickSave} />}
                  >
                    <IconSave />
                  </Button>
                )}

                {!isDesktopApplication() && (
                  <Button
                    css={isShowingSketchSearchModal && active}
                    onClick={this.toggleSketchSearchModal}
                    iconSize={24}
                    touchLabel={text.search}
                    tooltipPosition={TOOLTIP_POSITION}
                    tooltip={<TooltipContent title={text.search} />}
                  >
                    <CloudUploadIcon />
                  </Button>
                )}

                {isDesktopApplication() && (
                  <Button
                    onClick={this.openCreateSketchWindow}
                    iconSize={24}
                    touchLabel={text.touchLabels.createSketch}
                    tooltipPosition={TOOLTIP_POSITION}
                    tooltip={<TooltipContent title={text.createSketch} />}
                  >
                    <CreateNew />
                  </Button>
                )}

                <Separator />

                <Button
                  css={isShowingSettingsModal && active}
                  onClick={this.toggleSettingsModal}
                  aria-label={settingsTextConfig.heading}
                  iconSize={24}
                  touchLabel={settingsTextConfig.heading}
                  tooltipPosition={TOOLTIP_POSITION}
                  tooltip={<TooltipContent title={settingsTextConfig.heading} />}
                >
                  <IconCog />
                </Button>

                {sketchConfig.showLogoutButton && !isDesktopApplication() && !isMobileApplication() && (
                  <Button
                    onClick={this.logoutUser}
                    iconSize={24}
                    touchLabel={(isWorkbenchApplication()) ? text.return : text.logout}
                    tooltipPosition={TOOLTIP_POSITION}
                    tooltip={<TooltipContent title={(isWorkbenchApplication()) ? text.returnHelper : text.logoutHelper} />}
                    tooltipShow={highlightBack}
                  >
                    <IconLogout />
                  </Button>
                )}
              </Section>
            </Fragment>
          )}
          <input ref={this.inputOpenFileRef} type="file" css={hiddenInput} accept="image/*" onChange={this.handleFileChange} onClick={this.handleFileClick} />
        </AciHeader>
      </div>
    );
  }
}

export default connect(
  (state: RootState): StateProps => ({
    canUndo: historySelectors.canUndo(state),
    canRedo: historySelectors.canRedo(state),
    figures: figureSelectors.getAllFigures(state),
    isShowingToolbarLeft: sidebarSelectors.isShowingToolbarLeft(state),
    isShowingMobileMenu: sidebarSelectors.isShowingMobileMenu(state),
    isShowingGlaBreakdownModal: glaBreakdownModalSelectors.isShowing(state),
    isShowingSketchSearchModal: sketchSearchModalSelectors.isShowing(state),
    isShowingSettingsModal: settingsModalSelectors.isShowing(state),
    isShowingPageGroupsModal: pageGroupsModalSelectors.isShowing(state),
    isShowingPrintPreviewModal: printPreviewModalSelectors.isShowing(state),
    isShowingRescaleGridModal: resizeSketchModalSelectors.isRescaleShowing(state),
    isShowingNumpadModal: numpadModalSelectors.isShowing(state),
    isShowingLabelSidebar: sidebarSelectors.isShowingLabels(state),
    isShowingSymbolSidebar: sidebarSelectors.isShowingSymbols(state),
    isShowingPolygonSidebar: sidebarSelectors.isShowingPolygons(state),
    highlightBack: messageContainerSelectors.getMessage(state) === cannotGoBackDialog.text,
    getOrderId: orderDetailSelectors.getOrderId(state),
    getOrderAddress: orderDetailSelectors.getAddress(state),

  }),
  (dispatch: Dispatch): ActionProps => ({
    saveSketchToApi: async () => {
      await dispatch(editModeActions.clearSelection()); // hack to update svg before trying to use svg element
      dispatch(sketchPersistenceActions.saveSketch());
    },
    ...bindActionCreators(
      {
        clearSelection: editModeActions.clearSelection,
        loadSketch: sketchPersistenceActions.loadSketch,
        setSketch: sketchPersistenceActions.setSketch,
        autoSaveSketch: sketchPersistenceActions.autoSaveSketch,
        addImage: bluePrintImageActions.addImage,
        showNumpadModal: numpadModalActions.show,
        hideNumpadModal: numpadModalActions.hide,
        showGlaBreakdownModal: glaBreakdownModalActions.show,
        hideGlaBreakdownModal: glaBreakdownModalActions.hide,
        showSketchSearchModal: sketchSearchModalActions.show,
        hideSketchSearchModal: sketchSearchModalActions.hide,
        showSettingsModal: settingsModalActions.show,
        hideSettingsModal: settingsModalActions.hide,
        showPageGroupsModal: pageGroupsModalActions.show,
        hidePageGroupsModal: pageGroupsModalActions.hide,
        hidePrintPreviewModal: printPreviewModalActions.hide,
        hideSidebar: sidebarActions.hide,
        toggleToolbarLeft: sidebarActions.toggleToolbarLeft,
        toggleMobileMenu: sidebarActions.toggleMobileMenu,
        close: sidebarActions.hide,
        undo: historyActions.undo,
        redo: historyActions.redo,
        clearHistory: historyActions.clear,
        showMessage: messageContainerActions.show,
        toInspectionMessage: inspectionActions.toInspectionMessage,
        saveSketchLocal: inspectionActions.saveSketch,
        showKnownLengthPromptModal: resizeSketchModalActions.showKnownLengthPrompt,
        toggleIsTracingFirstWall: resizeSketchModalActions.toggleIsTracingFirstWall,
        showUnsavedModal: unsavedModalActions.show,
        resetCurrentSketch: sketchPersistenceActions.resetCurrentSketch,
        clearSketch: sketchActions.clear,
      },
      dispatch,
    ),
  }),
)(ToolbarTop);
